import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "motion/react";
import {
  X,
  User,
  Mail,
  Phone,
  MapPin,
  Calendar,
  CheckCircle,
  Clock,
  Building,
  Globe,
  Hash,
  Copy,
  ExternalLink,
} from "lucide-react";
import { toast } from "react-toastify";
import clientRequests from "../../api/ClientRequests";

const UserDetailsModal = ({ isOpen, onClose, userId }) => {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");

  useEffect(() => {
    if (isOpen && userId) {
      fetchUserDetails();
    } else {
      setUserData(null);
    }
  }, [isOpen, userId]);

  const fetchUserDetails = async () => {
    //console.log("Fetching user details for ID:", userId);
    //console.log("user details",)
    setLoading(true);
    try {
      // Adjust this API call based on your actual endpoint
      const response = await clientRequests.showUserDetails(userId);
      if (response.data.status_code === "000" && response.data.data) {
        setUserData(response.data.data);
        //console.log("User data fetched:", response.data.data);
      } else {
        toast.error(response.data.message || "Error fetching user details");
      }
    } catch (error) {
      console.error("API Error:", error);
      toast.error("Error fetching user details");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Not specified";
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "long",
        day: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return "Invalid Date";
    }
  };

  const copyToClipboard = (text) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    toast.success("Copied to clipboard!");
  };

  const getStatusBadge = (emailVerified) => {
    if (emailVerified) {
      return {
        label: "Verified",
        color: "bg-green-100 text-green-800 border-green-200",
        icon: CheckCircle,
      };
    }
    return {
      label: "Unverified",
      color: "bg-yellow-100 text-yellow-800 border-yellow-200",
      icon: Clock,
    };
  };

  const statusBadge = getStatusBadge(userData?.email_verified_at);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="flex fixed inset-0 z-50 justify-center items-center p-4 backdrop-blur-sm bg-black/50"
        onClick={onClose}
      >
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          transition={{ type: "spring", damping: 25, stiffness: 300 }}
          className="relative w-full max-w-4xl max-h-[90vh] overflow-hidden bg-white rounded-2xl shadow-2xl"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="relative p-6 text-white bg-gradient-to-r from-purple-600 via-blue-600 to-indigo-700">
            <div className="flex justify-between items-start">
              <div className="flex gap-4 items-center">
                <motion.div
                  initial={{ scale: 0 }}
                  animate={{ scale: 1 }}
                  transition={{ delay: 0.2, type: "spring", stiffness: 200 }}
                  className="flex justify-center items-center w-16 h-16 rounded-full backdrop-blur-sm bg-white/20"
                >
                  <User className="w-8 h-8" />
                </motion.div>
                <div>
                  <motion.h2
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3 }}
                    className="text-2xl font-bold"
                  >
                    User Details
                  </motion.h2>
                  <motion.p
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.4 }}
                    className="text-purple-100"
                  >
                    Comprehensive user information and profile details
                  </motion.p>
                </div>
              </div>

              <motion.button
                whileHover={{ scale: 1.1, rotate: 90 }}
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="p-2 transition-colors text-white/80 hover:text-white"
              >
                <X className="w-6 h-6" />
              </motion.button>
            </div>
          </div>

          {/* Loading State */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <motion.div
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
                className="w-8 h-8 rounded-full border-4 border-purple-200 border-t-purple-600"
              />
            </div>
          )}

          {/* Content */}
          {!loading && userData && (
            <div className="p-6 overflow-y-auto max-h-[calc(90vh-120px)]">
              {/* User Avatar and Basic Info */}
              <div className="mb-8">
                <div className="flex flex-col gap-6 items-start md:flex-row">
                  {/* Avatar Section */}
                  <motion.div
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.5 }}
                    className="flex-shrink-0"
                  >
                    <div className="relative">
                      <div className="flex justify-center items-center w-24 h-24 bg-gradient-to-br from-purple-500 to-pink-600 rounded-full shadow-lg">
                        <User className="w-12 h-12 text-white" />
                      </div>
                      <div className="absolute -right-2 -bottom-2">
                        <span
                          className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusBadge.color}`}
                        >
                          <statusBadge.icon className="w-3 h-3" />
                          {statusBadge.label}
                        </span>
                      </div>
                    </div>
                  </motion.div>

                  {/* Basic Info */}
                  <motion.div
                    initial={{ opacity: 0, x: 20 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.6 }}
                    className="flex-1"
                  >
                    <h3 className="mb-2 text-2xl font-bold text-gray-900">
                      {userData.full_name || userData.email || "Unknown User"}
                    </h3>
                    <div className="flex flex-wrap gap-2 mb-4">
                      <span className="inline-flex items-center px-3 py-1 text-sm font-medium text-gray-800 bg-gray-100 rounded-full border border-gray-200">
                        <Hash className="mr-1 w-4 h-4" />
                        ID: {userData.user_slug || userData.id || userId}
                      </span>
                    </div>
                  </motion.div>
                </div>
              </div>

              {/* Tabs */}
              <div className="mb-6">
                <div className="flex gap-1 p-1 bg-gray-100 rounded-lg">
                  {[
                    { id: "overview", label: "Overview", icon: User },
                    { id: "contact", label: "Contact", icon: Mail },
                    { id: "location", label: "Location", icon: MapPin },
                    { id: "activity", label: "Activity", icon: Calendar },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setActiveTab(tab.id)}
                      className={`flex gap-2 items-center px-4 py-2 rounded-md text-sm font-medium transition-all ${
                        activeTab === tab.id
                          ? "bg-white text-purple-600 shadow-sm"
                          : "text-gray-600 hover:text-gray-900"
                      }`}
                    >
                      <tab.icon className="w-4 h-4" />
                      {tab.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Tab Content */}
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeTab}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.3 }}
                >
                  {activeTab === "overview" && (
                    <div className="grid gap-6 md:grid-cols-2">
                      <div className="space-y-4">
                        <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                          <div className="flex gap-3 items-center mb-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <User className="w-5 h-5 text-blue-600" />
                            </div>
                            <h4 className="font-semibold text-gray-900">
                              Personal Information
                            </h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">Full Name:</span>
                              <span className="font-medium">
                                {userData.full_name || "Not specified"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">User ID:</span>
                              <span className="font-medium truncate">
                                {userData.user_slug || userData.id || userId}
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-gradient-to-br from-green-50 to-green-50 rounded-xl border border-green-100">
                          <div className="flex gap-3 items-center mb-3">
                            <div className="p-2 bg-green-100 rounded-lg">
                              <CheckCircle className="w-5 h-5 text-green-600" />
                            </div>
                            <h4 className="font-semibold text-gray-900">
                              Account Status
                            </h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Email Status:
                              </span>
                              <span
                                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${statusBadge.color}`}
                              >
                                <statusBadge.icon className="w-3 h-3" />
                                {statusBadge.label}
                              </span>
                            </div>
                            {userData.email_verified_at && (
                              <div className="flex justify-between">
                                <span className="text-gray-600">
                                  Verified At:
                                </span>
                                <span className="font-medium">
                                  {formatDate(userData.email_verified_at)}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      <div className="space-y-4">
                        <div className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                          <div className="flex gap-3 items-center mb-3">
                            <div className="p-2 bg-purple-100 rounded-lg">
                              <Calendar className="w-5 h-5 text-purple-600" />
                            </div>
                            <h4 className="font-semibold text-gray-900">
                              Timeline
                            </h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Member Since:
                              </span>
                              <span className="font-medium">
                                {formatDate(userData.created_at)}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-gray-600">
                                Account Age:
                              </span>
                              <span className="font-medium">
                                {Math.floor(
                                  (new Date() - new Date(userData.created_at)) /
                                    (1000 * 60 * 60 * 24)
                                )}{" "}
                                days
                              </span>
                            </div>
                          </div>
                        </div>

                        <div className="p-4 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                          <div className="flex gap-3 items-center mb-3">
                            <div className="p-2 bg-orange-100 rounded-lg">
                              <Hash className="w-5 h-5 text-orange-600" />
                            </div>
                            <h4 className="font-semibold text-gray-900">
                              System Info
                            </h4>
                          </div>
                          <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                              <span className="text-gray-600">User ID:</span>
                              <div className="flex gap-2 items-center">
                                <span className="px-2 py-1 font-mono text-xs bg-gray-100 rounded">
                                  {userId}
                                </span>
                                <button
                                  onClick={() => copyToClipboard(userId)}
                                  className="p-1 text-gray-400 hover:text-gray-600"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "contact" && (
                    <div className="space-y-6">
                      <div className="p-6 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-xl border border-blue-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-blue-100 rounded-lg">
                            <Mail className="w-6 h-6 text-blue-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Email Address
                            </h4>
                            <p className="text-sm text-gray-600">
                              Primary contact method
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3 items-center">
                          <span className="text-lg font-medium">
                            {userData.email}
                          </span>
                          <div className="flex gap-2">
                            <button
                              onClick={() => copyToClipboard(userData.email)}
                              className="p-2 text-gray-400 bg-white rounded-lg shadow-sm hover:text-gray-600"
                            >
                              <Copy className="w-4 h-4" />
                            </button>
                            <a
                              href={`mailto:${userData.email}`}
                              className="p-2 text-gray-400 bg-white rounded-lg shadow-sm hover:text-gray-600"
                            >
                              <ExternalLink className="w-4 h-4" />
                            </a>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 bg-gradient-to-br from-green-50 to-green-50 rounded-xl border border-green-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-green-100 rounded-lg">
                            <Phone className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Phone Number
                            </h4>
                            <p className="text-sm text-gray-600">
                              Direct contact number
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-3 items-center">
                          <span className="text-lg font-medium">
                            {userData.phone_number || "Not specified"}
                          </span>
                          {userData.phone_number && (
                            <div className="flex gap-2">
                              <button
                                onClick={() =>
                                  copyToClipboard(userData.phone_number)
                                }
                                className="p-2 text-gray-400 bg-white rounded-lg shadow-sm hover:text-gray-600"
                              >
                                <Copy className="w-4 h-4" />
                              </button>
                              <a
                                href={`tel:${userData.phone_number}`}
                                className="p-2 text-gray-400 bg-white rounded-lg shadow-sm hover:text-gray-600"
                              >
                                <ExternalLink className="w-4 h-4" />
                              </a>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "location" && (
                    <div className="space-y-6">
                      <div className="p-6 bg-gradient-to-br from-purple-50 to-pink-50 rounded-xl border border-purple-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-purple-100 rounded-lg">
                            <MapPin className="w-6 h-6 text-purple-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Location Details
                            </h4>
                            <p className="text-sm text-gray-600">
                              Geographic information
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                City
                              </label>
                              <p className="text-lg font-medium">
                                {userData.city || "Not specified"}
                              </p>
                            </div>
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                State/Province
                              </label>
                              <p className="text-lg font-medium">
                                {userData.state || "Not specified"}
                              </p>
                            </div>
                          </div>
                          <div className="space-y-3">
                            <div>
                              <label className="text-sm font-medium text-gray-600">
                                Address
                              </label>
                              <p className="text-lg font-medium">
                                {userData.address || "Not specified"}
                              </p>
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="p-6 bg-gradient-to-br from-orange-50 to-amber-50 rounded-xl border border-orange-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-orange-100 rounded-lg">
                            <Globe className="w-6 h-6 text-orange-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Regional Information
                            </h4>
                            <p className="text-sm text-gray-600">
                              Country and region details
                            </p>
                          </div>
                        </div>
                        <div className="flex gap-4 items-center">
                          <div className="flex gap-2 items-center">
                            <Building className="w-5 h-5 text-gray-400" />
                            <span className="font-medium">
                              {userData.city || "Unknown"}, {userData.state || "Unknown"}
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {activeTab === "activity" && (
                    <div className="space-y-6">
                      <div className="p-6 bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl border border-indigo-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-indigo-100 rounded-lg">
                            <Calendar className="w-6 h-6 text-indigo-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Account Timeline
                            </h4>
                            <p className="text-sm text-gray-600">
                              Important dates and milestones
                            </p>
                          </div>
                        </div>
                        <div className="space-y-4">
                          <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                            <div className="flex gap-3 items-center">
                              <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                              <span className="font-medium">
                                Account Created
                              </span>
                            </div>
                            <span className="text-sm text-gray-600">
                              {formatDate(userData.created_at)}
                            </span>
                          </div>
                          {userData.email_verified_at && (
                            <div className="flex justify-between items-center p-3 bg-white rounded-lg">
                              <div className="flex gap-3 items-center">
                                <div className="w-3 h-3 bg-green-500 rounded-full"></div>
                                <span className="font-medium">
                                  Email Verified
                                </span>
                              </div>
                              <span className="text-sm text-gray-600">
                                {formatDate(userData.email_verified_at)}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="p-6 bg-gradient-to-br from-green-50 to-green-50 rounded-xl border border-green-100">
                        <div className="flex gap-3 items-center mb-4">
                          <div className="p-3 bg-green-100 rounded-lg">
                            <Clock className="w-6 h-6 text-green-600" />
                          </div>
                          <div>
                            <h4 className="font-semibold text-gray-900">
                              Account Statistics
                            </h4>
                            <p className="text-sm text-gray-600">
                              Usage and activity metrics
                            </p>
                          </div>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <div className="p-4 text-center bg-white rounded-lg">
                            <div className="text-2xl font-bold text-purple-600">
                              {Math.floor(
                                (new Date() - new Date(userData.created_at)) /
                                  (1000 * 60 * 60 * 24)
                              )}
                            </div>
                            <div className="text-sm text-gray-600">
                              Days Active
                            </div>
                          </div>
                          <div className="p-4 text-center bg-white rounded-lg">
                            <div className="text-2xl font-bold text-green-600">
                              {userData.email_verified_at
                                ? "Verified"
                                : "Pending"}
                            </div>
                            <div className="text-sm text-gray-600">
                              Email Status
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          )}

          {/* Error State */}
          {!loading && !userData && (
            <div className="flex flex-col justify-center items-center py-12">
              <div className="flex justify-center items-center mb-4 w-16 h-16 bg-red-100 rounded-full">
                <X className="w-8 h-8 text-red-600" />
              </div>
              <h3 className="mb-2 text-lg font-medium text-gray-900">
                Unable to load user details
              </h3>
              <p className="text-center text-gray-600">
                Please try again later or contact support if the problem
                persists.
              </p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default UserDetailsModal;